import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Constants } from './manage-reconcile-constants';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';

import { Reconcilation } from '../interfaces/adjustments-reconciliation-service.interface';

import { AuthService } from '../../shared/services/auth/auth.service';
import { ManageReconciliationService } from '../services/manage-reconcilation/manage-reconcile.service';
import { deepCopy } from '../../create-surveys/utils/deep-copy';
import { EventEmitterService } from '../../shared/services/event-emitter/event-emitter.service';

@Component({
  selector: 'ps-ui-manage-reconcile',
  templateUrl: './manage-reconcile.component.html',
  styleUrls: ['./manage-reconcile.component.css'],
})
export class ManageReconcileComponent implements OnInit, OnDestroy {
  @Input() surveyId!: number | undefined;
  @Input() surveyStatus: number;
  operationType: string = Constants.OPERATION_TYPES.CLOSED;
  public reconcileFilesArrays: Array<Reconcilation> = [];
  public selectedReconcileFilesArray: Array<Reconcilation> = [];
  currentMonth: string = '';
  prevMonth: string = '';
  userId!: number;
  usrName!: string;
  desiredStatus?: number;

  public tabs: Tab[] = [];
  public activeTab!: Tab;
  public activeTabIndex!: number;
  public LIVE_RECO_STATUS_RADIO_BUTTONS = Constants.LIVE_RECO_STATUS_OPTIONS;
  public disableAcceptRadioButton = false;
  private _subscriptions: Subscription = new Subscription();

  constructor(
    private _manageReconciliationService: ManageReconciliationService,
    private _auth: AuthService,
    private _eventEmitterService: EventEmitterService
  ) {}

  ngOnInit(): void {
    if (this._auth.user?.id) {
      this.userId = this._auth.user.id;
      this.usrName = this._auth.user.usrName;
    }
    if (
      this._auth.buyerConfig?.reconciliations ||
      this._auth.userType === 'operator'
    ) {
      this.tabs = Constants.RECONCILATION_TABS.filter(
        (tab) => tab.title === 'RECONCILIATION'
      );
      this.setActiveTab(0);
    }
    this._getReconcileFilesData();
    this.getReconcileDate();
    this._changeSurveyStatusEmitter();
    this.disableAcceptRadioButton = this._disableAcceptRadioButton();
    this._setDesiredStatus();
  }

  private _getReconcileFilesData() {
    this._manageReconciliationService
      .getAdjustmentsFilesDataForSurvey(this.surveyId)
      .subscribe((adjustmentFilesData: Reconcilation[]) => {
        this.reconcileFilesArrays = adjustmentFilesData;
        if (
          this._auth.buyerConfig?.reconciliations &&
          this._auth.userType === 'buyer'
        ) {
          this.selectedReconcileFilesArray = adjustmentFilesData;
        } else {
          this._filterSelectedAdjustmentFiles();
        }
      });
  }

  private _filterSelectedAdjustmentFiles() {
    this.selectedReconcileFilesArray = deepCopy(
      this.reconcileFilesArrays.filter((file) =>
        [
          Constants.OPERATION_TYPES.CLOSED,
          Constants.OPERATION_TYPES.LIVE,
        ].includes(file.operationType)
      )
    );
  }

  getReconcileDate() {
    const dateFormat: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    };
    const modDate = new Date();
    /*********************Current month setting*******************************/
    this.currentMonth = new Date(
      modDate.getFullYear(),
      modDate.getMonth() + 2,
      0
    ).toLocaleDateString('en-us', dateFormat);
    /*********************Previous month setting*******************************/
    this.prevMonth = new Date(
      modDate.getFullYear(),
      modDate.getMonth() + 1,
      0
    ).toLocaleDateString('en-us', dateFormat);
  }

  public setActiveTab(activeTabIndex: number) {
    this.activeTabIndex = activeTabIndex;
    this.activeTab = this.tabs[activeTabIndex];
    if (activeTabIndex === 1) {
      this.operationType = Constants.OPERATION_TYPES.CLOSED;
      this.desiredStatus = this.LIVE_RECO_STATUS_RADIO_BUTTONS.ACCEPT;
    } else {
      this.operationType = Constants.OPERATION_TYPES.LIVE;
      this.desiredStatus = this.LIVE_RECO_STATUS_RADIO_BUTTONS.REJECT;
    }
    this._filterSelectedAdjustmentFiles();
  }

  private _changeSurveyStatusEmitter() {
    this._subscriptions.add(
      this._eventEmitterService.changeSurveyStatusEmitter.subscribe((data) => {
        this.surveyStatus = data.status;
        this.disableAcceptRadioButton = !!this._disableAcceptRadioButton();
        this._setDesiredStatus();
      })
    );
  }

  setOperationType() {
    if (!this.desiredStatus) {
      this.operationType = Constants.OPERATION_TYPES.CLOSED;
      this.desiredStatus = this.LIVE_RECO_STATUS_RADIO_BUTTONS.ACCEPT;
    } else {
      this.operationType = Constants.OPERATION_TYPES.LIVE;
      this.desiredStatus = this.LIVE_RECO_STATUS_RADIO_BUTTONS.REJECT;
    }
  }

  private _setDesiredStatus() {
    if (
      this.surveyStatus === SURVEY_STATUS.CLOSED ||
      this.surveyStatus === SURVEY_STATUS.INVOICE
    ) {
      this.desiredStatus = this.LIVE_RECO_STATUS_RADIO_BUTTONS.ACCEPT;
      this.setOperationType();
    }
  }

  private _disableAcceptRadioButton() {
    return [SURVEY_STATUS.LIVE, SURVEY_STATUS.PAUSED].includes(
      this.surveyStatus
    );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}

interface Tab {
  title: string;
  active: boolean;
}
